import {
    PitcherProfile as PitcherProfileType,
    PitcherGameLog,
    BullpenSessionSummary,
    PerformanceSummary,
} from '@pitch-tracker/shared';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import HeatZoneOverlay from '../../components/live/HeatZoneOverlay';
import { PerformanceSummaryCard } from '../../components/performanceSummary';
import { GameLogTable, GameLogDetail, BullpenLogTable, BullpenLogDetail } from '../../components/pitcher';
import useHeatZones from '../../hooks/useHeatZones';
import api from '../../services/api';
import { bullpenService } from '../../services/bullpenService';
import { performanceSummaryService } from '../../services/performanceSummaryService';
import PitchEffectivenessCard from './PitchEffectivenessCard';
import {
    Container,
    Header,
    HeaderLeft,
    BackButton,
    ReportButton,
    ProfileInfo,
    PitcherName,
    PitcherMeta,
    JerseyNumber,
    TeamName,
    Content,
    CareerStatsCard,
    CareerStatsTitle,
    CareerStatsGrid,
    StatBox,
    StatValue,
    StatLabel,
    PitchTypesCard,
    PitchTypesTitle,
    PitchTypesList,
    PitchTypeBadge,
    GameLogsSection,
    SectionHeader,
    SectionTitle,
    LoadingText,
    ErrorText,
    ErrorContainer,
    HeatZoneCard,
    HeatZoneHeader,
    HeatZoneTitle,
    ToggleButton,
    HeatZoneContent,
    HeatZoneLegend,
    LegendItem,
    LegendColor,
    PitchTypeFilter,
    PitchTypeFilterLabel,
    PitchTypeSelect,
} from './styles';

const formatPitchType = (type: string): string => {
    const names: { [key: string]: string } = {
        fastball: 'Fastball',
        '2-seam': '2-Seam',
        '4-seam': '4-Seam',
        cutter: 'Cutter',
        sinker: 'Sinker',
        slider: 'Slider',
        curveball: 'Curveball',
        changeup: 'Changeup',
        splitter: 'Splitter',
        knuckleball: 'Knuckleball',
        screwball: 'Screwball',
        other: 'Other',
    };
    return names[type] || type;
};

const PitcherProfile: React.FC = () => {
    const navigate = useNavigate();
    const { team_id, pitcher_id } = useParams<{ team_id: string; pitcher_id: string }>();

    const [profile, setProfile] = useState<PitcherProfileType | null>(null);
    const [selectedGame, setSelectedGame] = useState<PitcherGameLog | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showHeatZones, setShowHeatZones] = useState(true);
    const [heatZonePitchType, setHeatZonePitchType] = useState<string | undefined>(undefined);
    const [bullpenSessions, setBullpenSessions] = useState<BullpenSessionSummary[]>([]);
    const [selectedBullpen, setSelectedBullpen] = useState<BullpenSessionSummary | null>(null);
    const [performanceSummaries, setPerformanceSummaries] = useState<PerformanceSummary[]>([]);
    const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
    const [generatingSummaryId, setGeneratingSummaryId] = useState<string | null>(null);
    const summariesSectionRef = useRef<HTMLDivElement>(null);

    // Fetch heat zones for career stats (no gameId = all games, optional pitch type filter)
    const { zones: heatZones } = useHeatZones(pitcher_id, undefined, heatZonePitchType);

    useEffect(() => {
        const loadProfile = async () => {
            if (!pitcher_id) return;

            try {
                setLoading(true);
                const [profileRes, bullpenRes, summariesRes] = await Promise.all([
                    api.get<{ profile: PitcherProfileType }>(`/analytics/pitcher/${pitcher_id}/profile`),
                    bullpenService.getPitcherBullpenLogs(pitcher_id).catch(() => ({ sessions: [], total_count: 0 })),
                    performanceSummaryService.getPitcherSummaries(pitcher_id, 5).catch(() => ({ summaries: [], total_count: 0 })),
                ]);
                setProfile(profileRes.data.profile);
                setBullpenSessions(bullpenRes.sessions);
                setPerformanceSummaries(summariesRes.summaries);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load pitcher profile');
            } finally {
                setLoading(false);
            }
        };

        loadProfile();
    }, [pitcher_id]);

    const handleGameSelect = (gameLog: PitcherGameLog) => {
        setSelectedGame(gameLog);
    };

    const handleCloseDetail = () => {
        setSelectedGame(null);
    };

    const handleViewGame = (gameId: string) => {
        navigate(`/game/${gameId}`);
    };

    const summaryGameIds = new Set(performanceSummaries.filter((s) => s.source_type === 'game').map((s) => s.source_id));

    const handleViewSummary = (_gameId: string) => {
        summariesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleGenerateSummary = async (gameId: string) => {
        setGeneratingSummaryId(gameId);
        try {
            const summary = await performanceSummaryService.getSummary('game', gameId, pitcher_id);
            setPerformanceSummaries((prev) => {
                const exists = prev.some((s) => s.id === summary.id);
                return exists ? prev.map((s) => (s.id === summary.id ? summary : s)) : [summary, ...prev];
            });
        } finally {
            setGeneratingSummaryId(null);
        }
    };

    if (loading) {
        return (
            <Container>
                <LoadingText>Loading pitcher profile...</LoadingText>
            </Container>
        );
    }

    if (error || !profile) {
        return (
            <Container>
                <ErrorContainer>
                    <ErrorText>{error || 'Pitcher not found'}</ErrorText>
                    <BackButton onClick={() => navigate(`/teams/${team_id}`)}>Back to Team</BackButton>
                </ErrorContainer>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <HeaderLeft>
                    <BackButton onClick={() => navigate(`/teams/${team_id}`)}>&larr; Back to Team</BackButton>
                    <ProfileInfo>
                        <PitcherName>
                            {profile.first_name} {profile.last_name}
                        </PitcherName>
                        <PitcherMeta>
                            <JerseyNumber>#{profile.jersey_number || '-'}</JerseyNumber>
                            <span>|</span>
                            <TeamName>{profile.team_name}</TeamName>
                            <span>|</span>
                            <span>Throws: {profile.throws === 'R' ? 'Right' : 'Left'}</span>
                        </PitcherMeta>
                    </ProfileInfo>
                </HeaderLeft>
                <ReportButton onClick={() => navigate(`/teams/${team_id}/pitcher/${pitcher_id}/report`)}>
                    View Performance Report →
                </ReportButton>
            </Header>

            <Content>
                <CareerStatsCard>
                    <CareerStatsTitle>Career Stats</CareerStatsTitle>
                    <CareerStatsGrid>
                        <StatBox>
                            <StatValue>{profile.career_stats.total_games}</StatValue>
                            <StatLabel>Games</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue>{profile.career_stats.total_pitches}</StatValue>
                            <StatLabel>Total Pitches</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue>{profile.career_stats.total_batters_faced}</StatValue>
                            <StatLabel>Batters Faced</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue highlight>{profile.career_stats.overall_strike_percentage}%</StatValue>
                            <StatLabel>Strike %</StatLabel>
                        </StatBox>
                        <StatBox>
                            <StatValue>
                                {profile.career_stats.overall_target_accuracy !== null
                                    ? `${profile.career_stats.overall_target_accuracy}%`
                                    : '-'}
                            </StatValue>
                            <StatLabel>Target Accuracy</StatLabel>
                        </StatBox>
                    </CareerStatsGrid>
                </CareerStatsCard>

                {profile.pitch_types.length > 0 && (
                    <PitchTypesCard>
                        <PitchTypesTitle>Pitch Arsenal</PitchTypesTitle>
                        <PitchTypesList>
                            {profile.pitch_types.map((type) => (
                                <PitchTypeBadge key={type}>{formatPitchType(type)}</PitchTypeBadge>
                            ))}
                        </PitchTypesList>
                    </PitchTypesCard>
                )}

                <HeatZoneCard>
                    <HeatZoneHeader>
                        <HeatZoneTitle>Strike Zone Heat Map</HeatZoneTitle>
                        <ToggleButton active={showHeatZones} onClick={() => setShowHeatZones(!showHeatZones)}>
                            {showHeatZones ? 'Hide' : 'Show'} Heat Zones
                        </ToggleButton>
                    </HeatZoneHeader>
                    {profile.pitch_types.length > 0 && (
                        <PitchTypeFilter>
                            <PitchTypeFilterLabel>Filter by pitch:</PitchTypeFilterLabel>
                            <PitchTypeSelect
                                value={heatZonePitchType || ''}
                                onChange={(e) => setHeatZonePitchType(e.target.value || undefined)}
                            >
                                <option value="">All Pitches</option>
                                {profile.pitch_types.map((type) => (
                                    <option key={type} value={type}>
                                        {formatPitchType(type)}
                                    </option>
                                ))}
                            </PitchTypeSelect>
                        </PitchTypeFilter>
                    )}
                    <HeatZoneContent>
                        <svg viewBox="0 0 300 300" style={{ width: '100%', maxWidth: '350px' }}>
                            {/* Background */}
                            <rect x="0" y="0" width="300" height="300" fill="var(--surface-elevated)" />
                            {/* Strike zone grid — coordinates match HeatZoneOverlay constants */}
                            <g transform="translate(105, 100)">
                                <rect x="0" y="0" width="90" height="132" fill="var(--surface-card)" />
                                {[0, 1, 2].map((row) =>
                                    [0, 1, 2].map((col) => (
                                        <rect
                                            key={`cell-${row}-${col}`}
                                            x={col * 30}
                                            y={row * 44}
                                            width={30}
                                            height={44}
                                            fill="var(--surface-border)"
                                            stroke="var(--surface-border-strong)"
                                            strokeWidth="1"
                                        />
                                    ))
                                )}
                                <rect
                                    x="0"
                                    y="0"
                                    width="90"
                                    height="132"
                                    fill="none"
                                    stroke="var(--surface-text-subtle)"
                                    strokeWidth="2"
                                />
                            </g>
                            {/* Heat zone overlay */}
                            <HeatZoneOverlay zones={heatZones} visible={showHeatZones} />
                        </svg>
                    </HeatZoneContent>
                    <HeatZoneLegend>
                        <LegendItem>
                            <LegendColor color="#ef4444" />
                            <span>Low Strike %</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#eab308" />
                            <span>50%</span>
                        </LegendItem>
                        <LegendItem>
                            <LegendColor color="#22c55e" />
                            <span>High Strike %</span>
                        </LegendItem>
                    </HeatZoneLegend>
                </HeatZoneCard>

                {pitcher_id && <PitchEffectivenessCard pitcherId={pitcher_id} />}

                <GameLogsSection>
                    <SectionHeader>
                        <SectionTitle>Game Logs</SectionTitle>
                    </SectionHeader>
                    <GameLogTable
                        gameLogs={profile.game_logs}
                        onGameSelect={handleGameSelect}
                        onGenerateSummary={handleGenerateSummary}
                        generatingSummaryId={generatingSummaryId}
                        summaryGameIds={summaryGameIds}
                        onViewSummary={handleViewSummary}
                    />
                </GameLogsSection>

                <GameLogsSection>
                    <SectionHeader>
                        <SectionTitle>Bullpen Sessions</SectionTitle>
                    </SectionHeader>
                    <BullpenLogTable sessions={bullpenSessions} onSessionSelect={setSelectedBullpen} />
                </GameLogsSection>

                {performanceSummaries.length > 0 && (
                    <GameLogsSection ref={summariesSectionRef}>
                        <SectionHeader>
                            <SectionTitle>Performance Summaries</SectionTitle>
                        </SectionHeader>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {performanceSummaries.map((s) => (
                                <PerformanceSummaryCard
                                    key={s.id}
                                    summary={s}
                                    onRegenerate={async () => {
                                        setRegeneratingId(s.id);
                                        try {
                                            const updated = await performanceSummaryService.regenerateNarrative(s.id);
                                            setPerformanceSummaries((prev) =>
                                                prev.map((ps) => (ps.id === updated.id ? updated : ps))
                                            );
                                        } finally {
                                            setRegeneratingId(null);
                                        }
                                    }}
                                    regenerating={regeneratingId === s.id}
                                />
                            ))}
                        </div>
                    </GameLogsSection>
                )}
            </Content>
            {selectedGame && <GameLogDetail gameLog={selectedGame} onClose={handleCloseDetail} onViewGame={handleViewGame} />}
            {selectedBullpen && <BullpenLogDetail session={selectedBullpen} onClose={() => setSelectedBullpen(null)} />}
        </Container>
    );
};

export default PitcherProfile;
